import {
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemButton,
    Collapse,
    Box,
    useTheme,
    useMediaQuery,
    IconButton,
    Typography,
    Divider,
    Tooltip
} from '@mui/material';
import {
    Dashboard as DashboardIcon,
    Person as PersonIcon,
    Schedule as ScheduleIcon,
    Payment as PaymentIcon,
    History as HistoryIcon,
    AdminPanelSettings,
    ExpandLess,
    ExpandMore,
    People as PeopleIcon,
    MenuBook as GuideIcon,
    Refresh as RefreshIcon,
    ContactSupport as ContactIcon
} from '@mui/icons-material';
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { isMembershipValid } from '../../utils/membershipUtils';
import ContactPopup from '../popups/ContactPopup';

const drawerWidth = 240;

const Sidebar = ({ open, onClose, variant = "persistent" }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { user, logout, refreshUser } = useAuth();
    const [adminOpen, setAdminOpen] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [contactDialogOpen, setContactDialogOpen] = useState(false);

    const isAdmin = user?.membershipType === 'Membership Administrator' && isMembershipValid(user);

    // Check if membership is currently valid (contract active AND within start and end dates)
    const hasValidMembership = user?.membershipType && isMembershipValid(user);

    const handleAdminClick = () => {
        setAdminOpen(!adminOpen);
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await refreshUser();
        setRefreshing(false);
    };

    const adminMenuItems = [
        {
            text: 'Clients',
            icon: <PeopleIcon />,
            path: '/admin/clients'
        },
        {
            text: 'Children',
            icon: <PeopleIcon />,
            path: '/admin/children'
        },
        {
            text: 'Admin Guide',
            icon: <GuideIcon />,
            path: '/admin/guide'
        }
    ];

    const regularMenuItems = [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
        { text: 'Profile', icon: <PersonIcon />, path: '/profile' },
        { text: 'Children', icon: <PeopleIcon />, path: '/children' },
        { text: 'Fees', icon: <PaymentIcon />, path: '/fees' },
        { text: 'Contact TTE', icon: <ContactIcon />, onClick: () => setContactDialogOpen(true) },
    ];

    const handleNavigation = (path) => {
        if (path) {
            navigate(path);
            if (isMobile) {
                onClose();
            }
        }
    };

    return (
        <Drawer
            variant={variant}
            anchor="left"
            open={open}
            onClose={onClose}
            sx={{
                width: drawerWidth,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                    width: drawerWidth,
                    boxSizing: 'border-box',
                    mt: { xs: 7, sm: 8 },
                    height: { xs: 'calc(100% - 56px)', sm: 'calc(100% - 64px)' },
                    borderRight: `1px solid ${theme.palette.divider}`,
                },
            }}
        >
            <Box sx={{ overflow: 'auto' }}>
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                        <Typography variant="subtitle1" noWrap>
                            {user?.firstName} {user?.lastName}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" noWrap>
                            {hasValidMembership ? user?.membershipType : 'No Active Membership'}
                        </Typography>
                    </Box>
                    <Tooltip title="Refresh user data">
                        <IconButton 
                            onClick={handleRefresh} 
                            disabled={refreshing}
                            size="small"
                        >
                            <RefreshIcon 
                                sx={{ 
                                    animation: refreshing ? 'spin 1s linear infinite' : 'none',
                                    '@keyframes spin': {
                                        '0%': { transform: 'rotate(0deg)' },
                                        '100%': { transform: 'rotate(360deg)' }
                                    }
                                }} 
                            />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Divider />
                <List>
                    {regularMenuItems.map((item) => (
                        <ListItem key={item.text} disablePadding>
                            <ListItemButton
                                selected={location.pathname === item.path}
                                onClick={() => item.onClick ? item.onClick() : handleNavigation(item.path)}
                                sx={{
                                    minHeight: 48,
                                    px: 2.5,
                                    '&.Mui-selected': {
                                        backgroundColor: theme.palette.primary.light,
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: theme.palette.primary.main,
                                        },
                                        '& .MuiListItemIcon-root': {
                                            color: 'white',
                                        },
                                    },
                                }}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 0,
                                        mr: 3,
                                        justifyContent: 'center',
                                        color: location.pathname === item.path ? 'white' : 'inherit',
                                    }}
                                >
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText primary={item.text} />
                            </ListItemButton>
                        </ListItem>
                    ))}

                    {isAdmin && (
                        <>
                            <ListItem disablePadding>
                                <ListItemButton onClick={handleAdminClick}>
                                    <ListItemIcon sx={{ minWidth: 0, mr: 3 }}>
                                        <AdminPanelSettings />
                                    </ListItemIcon>
                                    <ListItemText primary="Admin" />
                                    {adminOpen ? <ExpandLess /> : <ExpandMore />}
                                </ListItemButton>
                            </ListItem>
                            <Collapse in={adminOpen} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    {adminMenuItems.map((item) => (
                                        <ListItem key={item.text} disablePadding>
                                            <ListItemButton
                                                selected={location.pathname === item.path}
                                                onClick={() => handleNavigation(item.path)}
                                                sx={{
                                                    pl: 4,
                                                    '&.Mui-selected': {
                                                        backgroundColor: theme.palette.primary.light,
                                                        color: 'white',
                                                        '&:hover': {
                                                            backgroundColor: theme.palette.primary.main,
                                                        },
                                                        '& .MuiListItemIcon-root': {
                                                            color: 'white',
                                                        },
                                                    },
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={{
                                                        minWidth: 0,
                                                        mr: 3,
                                                        justifyContent: 'center',
                                                        color: location.pathname === item.path ? 'white' : 'inherit',
                                                    }}
                                                >
                                                    {item.icon}
                                                </ListItemIcon>
                                                <ListItemText primary={item.text} />
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </List>
                            </Collapse>
                        </>
                    )}
                </List>

                <ContactPopup
                    open={contactDialogOpen}
                    onClose={() => setContactDialogOpen(false)}
                />
            </Box>
        </Drawer>
    );
};

export default Sidebar; 